import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../../utils/services/authService';
import { useAuth } from '../../context/AuthContext';
import { DEFAULT_LANDING_ROUTE } from '../../routes/destinations';

const FREE_TIER = 'FREE_TIER';
const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;

const RegisterModal = ({ onClose, onSwitchToLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { loginWithToken } = useAuth();
  const navigate = useNavigate();

  const isPasswordStrong = PASSWORD_REGEX.test(password);
  const isFormValid =
    email && password && confirmPassword && termsAccepted && password === confirmPassword && isPasswordStrong;

  // Lock body scroll.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Dismiss on Escape.
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (!isPasswordStrong) {
      setError('Password must be at least 8 characters with 1 uppercase, 1 lowercase, and 1 number');
      return;
    }

    setIsLoading(true);

    try {
      const result = await authService.register(email, password, FREE_TIER, { marketingConsent });

      if (result.success && result.token) {
        const hydrated = loginWithToken(result.token);
        onClose();
        if (hydrated) {
          navigate(DEFAULT_LANDING_ROUTE, { replace: true });
        }
        return;
      }

      setError(result.message || 'Registration failed. Please try again.');
      setIsLoading(false);
    } catch {
      setError('An unexpected error occurred. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-xs rounded-xl border border-slate-700/60 bg-[#0b1120] p-5 shadow-2xl animate-modal-in">
        <style>{`
          @keyframes modal-in {
            from { opacity: 0; transform: scale(0.96) translateY(-10px); }
            to   { opacity: 1; transform: scale(1) translateY(0); }
          }
          .animate-modal-in { animation: modal-in 0.15s ease-out forwards; }
        `}</style>

        {/* Close */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close modal"
          className="absolute right-3 top-3 text-slate-500 transition-colors duration-150 hover:text-white"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Logo */}
        <div className="mb-3 flex justify-center">
          <img src="/favicon.svg" alt="Brownlow Genius" className="h-12 w-auto" />
        </div>

        <h2 className="mb-4 text-center text-sm font-bold text-white">Create Your Account</h2>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-3" noValidate>
          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-2.5 py-1.5">
              <svg className="h-3.5 w-3.5 shrink-0 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-xs text-red-400">{error}</p>
            </div>
          )}

          {/* Email */}
          <div>
            <label htmlFor="register-email" className="mb-1 block text-xs font-medium text-slate-400">
              Email
            </label>
            <input
              id="register-email"
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); if (error) setError(''); }}
              placeholder="you@example.com"
              autoComplete="email"
              autoFocus
              required
              className="w-full rounded-lg border border-slate-700/80 bg-slate-900/60 px-3 py-2 text-sm text-white placeholder-slate-500 transition duration-150 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500/40"
            />
          </div>

          {/* Password */}
          <div>
            <label htmlFor="register-password" className="mb-1 block text-xs font-medium text-slate-400">
              Password
            </label>
            <input
              id="register-password"
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (error === 'Passwords do not match' && confirmPassword && e.target.value === confirmPassword) setError('');
              }}
              placeholder="••••••••"
              autoComplete="new-password"
              required
              className="w-full rounded-lg border border-slate-700/80 bg-slate-900/60 px-3 py-2 text-sm text-white placeholder-slate-500 transition duration-150 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500/40"
            />
            <p className={`mt-0.5 text-[10px] leading-tight ${password && !isPasswordStrong ? 'text-red-400' : 'text-slate-500'}`}>
              Min 8 characters · 1 uppercase · 1 lowercase · 1 number
            </p>
          </div>

          {/* Confirm Password */}
          <div>
            <label htmlFor="register-confirm-password" className="mb-1 block text-xs font-medium text-slate-400">
              Confirm Password
            </label>
            <input
              id="register-confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (e.target.value && password && e.target.value !== password) {
                  setError('Passwords do not match');
                } else {
                  setError('');
                }
              }}
              placeholder="••••••••"
              autoComplete="new-password"
              required
              className="w-full rounded-lg border border-slate-700/80 bg-slate-900/60 px-3 py-2 text-sm text-white placeholder-slate-500 transition duration-150 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500/40"
            />
          </div>

          {/* Marketing consent (optional) */}
          <div className="flex items-start gap-2">
            <input
              id="register-marketing"
              type="checkbox"
              checked={marketingConsent}
              onChange={(e) => setMarketingConsent(e.target.checked)}
              className="mt-0.5 h-3.5 w-3.5 rounded border-slate-700 bg-slate-900/60 text-emerald-500 focus:ring-1 focus:ring-emerald-500/40"
            />
            <label htmlFor="register-marketing" className="text-[11px] leading-snug text-slate-400">
              Send me email updates about future Brownlow Genius products and features
            </label>
          </div>

          {/* Terms */}
          <div className="flex items-start gap-2">
            <input
              id="register-terms"
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              required
              className="mt-0.5 h-3.5 w-3.5 rounded border-slate-700 bg-slate-900/60 text-emerald-500 focus:ring-1 focus:ring-emerald-500/40"
            />
            <label htmlFor="register-terms" className="text-[11px] leading-snug text-slate-400">
              I agree to the{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:text-emerald-300">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:text-emerald-300">
                Privacy Policy
              </a>
            </label>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isLoading || !isFormValid}
            className="!mt-4 flex w-full items-center justify-center rounded-lg bg-emerald-600 py-2 text-sm font-semibold text-white transition duration-150 hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isLoading ? (
              <>
                <svg className="-ml-1 mr-2 h-4 w-4 animate-spin text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Creating Account…
              </>
            ) : (
              'Create Free Account'
            )}
          </button>
        </form>

        {/* Switch to login */}
        <div className="mt-3 text-center">
          <p className="text-xs text-slate-500">
            Already have an account?{' '}
            <button
              type="button"
              onClick={() => { onClose(); if (onSwitchToLogin) onSwitchToLogin(); }}
              className="font-medium text-emerald-400 transition-colors duration-150 hover:text-emerald-300"
            >
              Sign in
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterModal;
